import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import "../style/SensorDetailPage.css";
import {
    ArrowLeft,
    Activity,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    Clock,
    MapPin,
    Thermometer,
    Droplets,
    Sun,
    Wifi,
    WifiOff,
    Download,
    RefreshCw,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    AreaChart,
} from "recharts";
import * as XLSX from 'xlsx';

const UNIDADES = {
    temperatura: "°C",
    humedad: "%",
    luz: "lx",
    ph: "pH",
};

const ICONOS_TIPO = {
    temperatura: Thermometer,
    humedad: Droplets,
    luz: Sun,
    ph: Activity,
};

const OPENAI_API_KEY = "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";

export default function SensorDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [menuLateralAbierto, setMenuLateralAbierto] = useState(false);
    const [sensor, setSensor] = useState(null);
    const [datos, setDatos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [rangoTiempo, setRangoTiempo] = useState("24h");
    const [actualizando, setActualizando] = useState(false);
    const [exportando, setExportando] = useState(false);

    const cargarDatosSensor = async () => {
        try {
            const token = localStorage.getItem("token");

            const resSensor = await fetch(
                "https://agro-nexus-prod.vercel.app//api/usuario/sensores-datos",
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const jsonSensor = await resSensor.json();

            if (jsonSensor.success) {
                const sensorEncontrado = jsonSensor.sensores.find(
                    (s) => s.sensor_id === parseInt(id)
                );
                if (sensorEncontrado) {
                    setSensor(sensorEncontrado);
                }
            }

            const limite = rangoTiempo === "24h" ? 100 : rangoTiempo === "7d" ? 500 : 1000;
            const resDatos = await fetch(
                `https://agro-nexus-prod.vercel.app/api/sensores/${id}/datos?limite=${limite}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const jsonDatos = await resDatos.json();

            if (jsonDatos.success) {
                setDatos(jsonDatos.datos.reverse());
            }
        } catch (err) {
            console.error("Error cargando datos:", err);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargarDatosSensor();
        const intervalo = setInterval(cargarDatosSensor, 30000);
        return () => clearInterval(intervalo);
    }, [id, rangoTiempo]);

    const handleRefresh = async () => {
        setActualizando(true);
        await cargarDatosSensor();
        setTimeout(() => setActualizando(false), 500);
    };

    const obtenerAnalisisIA = async (datosResumen) => {
        try {
            const prompt = `Eres un experto agrónomo especializado en análisis de datos de sensores agrícolas. 

Analiza los siguientes datos del sensor:
- Tipo de sensor: ${datosResumen.tipo}
- Valor actual: ${datosResumen.valorActual} ${datosResumen.unidad}
- Promedio: ${datosResumen.promedio} ${datosResumen.unidad}
- Mínimo: ${datosResumen.minimo} ${datosResumen.unidad}
- Máximo: ${datosResumen.maximo} ${datosResumen.unidad}
- Número de lecturas: ${datosResumen.numLecturas}
- Rango configurado: ${datosResumen.rangoMin} - ${datosResumen.rangoMax} ${datosResumen.unidad}
- Estado: ${datosResumen.estado}
- Tendencia: ${datosResumen.tendencia}

Proporciona un análisis detallado en formato JSON con la siguiente estructura:
{
  "resumenGeneral": "Breve resumen del estado actual del sensor (2-3 líneas)",
  "analisisDetallado": [
    "Punto de análisis 1",
    "Punto de análisis 2",
    "Punto de análisis 3"
  ],
  "recomendaciones": [
    "Recomendación práctica 1",
    "Recomendación práctica 2",
    "Recomendación práctica 3"
  ],
  "alertas": [
    "Alerta o precaución importante 1",
    "Alerta o precaución importante 2"
  ],
  "accionesInmediatas": [
    "Acción inmediata 1 si aplica",
    "Acción inmediata 2 si aplica"
  ]
}

Sé específico, práctico y enfócate en acciones concretas que un agricultor pueda tomar.`;

            const response = await fetch('https://api.openai.com/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${OPENAI_API_KEY}`
                },
                body: JSON.stringify({
                    model: 'gpt-3.5-turbo',
                    messages: [
                        { role: 'system', content: 'Eres un experto agrónomo que proporciona análisis claros y prácticos.' },
                        { role: 'user', content: prompt }
                    ],
                    temperature: 0.7,
                    max_tokens: 1500
                })
            });

            if (!response.ok) {
                throw new Error('Error al obtener análisis de IA');
            }

            const data = await response.json();
            const contenido = data.choices[0].message.content;

            try {
                return JSON.parse(contenido);
            } catch {

                return {
                    resumenGeneral: contenido.substring(0, 200),
                    analisisDetallado: ["Análisis generado por IA disponible en el documento"],
                    recomendaciones: ["Consulte el análisis completo en el documento"],
                    alertas: [],
                    accionesInmediatas: []
                };
            }
        } catch (error) {
            console.error('Error obteniendo análisis IA:', error);
            return {
                resumenGeneral: "No se pudo obtener el análisis de IA. Revise los datos manualmente.",
                analisisDetallado: ["Error al conectar con el servicio de análisis"],
                recomendaciones: ["Revise manualmente los datos del sensor"],
                alertas: ["No se pudo generar análisis automático"],
                accionesInmediatas: []
            };
        }
    };

    const exportarDatos = async () => {
        if (datos.length === 0) {
            alert("No hay datos para exportar");
            return;
        }

        setExportando(true);

        try {
            const valores = datos.map((d) => parseFloat(d.valor)).filter((v) => !isNaN(v));
            const promedio = valores.length
                ? (valores.reduce((a, b) => a + b, 0) / valores.length).toFixed(2)
                : "--";
            const maximo = valores.length ? Math.max(...valores).toFixed(2) : "--";
            const minimo = valores.length ? Math.min(...valores).toFixed(2) : "--";

            let tendencia = "Estable";
            if (valores.length >= 2) {
                const inicio = valores.slice(0, Math.floor(valores.length / 2));
                const fin = valores.slice(Math.floor(valores.length / 2));
                const promedioInicio = inicio.reduce((a, b) => a + b, 0) / inicio.length;
                const promedioFin = fin.reduce((a, b) => a + b, 0) / fin.length;
                tendencia = promedioFin > promedioInicio ? "Ascendente" : "Descendente";
            }

            const unidad = UNIDADES[sensor.tipo_sensor?.toLowerCase()] || "";

            const datosResumen = {
                tipo: sensor.tipo_sensor,
                valorActual: sensor.valor ?? "--",
                unidad: unidad,
                promedio: promedio,
                minimo: minimo,
                maximo: maximo,
                numLecturas: datos.length,
                rangoMin: sensor.valor_minimo ?? "No definido",
                rangoMax: sensor.valor_maximo ?? "No definido",
                estado: sensor.estado,
                tendencia: tendencia
            };

            const analisisIA = await obtenerAnalisisIA(datosResumen);

            const wb = XLSX.utils.book_new();

            const resumenData = [
                ["REPORTE DE ANÁLISIS DE SENSOR"],
                [""],
                ["Información del Sensor"],
                ["Nombre del Sensor:", sensor.sensor],
                ["Tipo:", sensor.tipo_sensor],
                ["Ubicación:", sensor.terreno],
                ["Estado:", sensor.estado],
                ["Fecha de Generación:", new Date().toLocaleString()],
                [""],
                ["Estadísticas Generales"],
                ["Valor Actual:", `${sensor.valor ?? "--"} ${unidad}`],
                ["Promedio:", `${promedio} ${unidad}`],
                ["Valor Mínimo:", `${minimo} ${unidad}`],
                ["Valor Máximo:", `${maximo} ${unidad}`],
                ["Total de Lecturas:", datos.length],
                ["Tendencia:", tendencia],
                [""],
                ["Configuración del Sensor"],
                ["Rango Mínimo:", sensor.valor_minimo ? `${sensor.valor_minimo} ${unidad}` : "No definido"],
                ["Rango Máximo:", sensor.valor_maximo ? `${sensor.valor_maximo} ${unidad}` : "No definido"],
                ["Última Actualización:", sensor.fecha_hora ? new Date(sensor.fecha_hora).toLocaleString() : "Sin datos"]
            ];

            const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
            wsResumen['!cols'] = [{ wch: 25 }, { wch: 40 }];
            XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen");

            const analisisData = [
                ["ANÁLISIS INTELIGENTE DEL SENSOR"],
                [""],
                ["RESUMEN GENERAL"],
                [analisisIA.resumenGeneral],
                [""],
                ["ANÁLISIS DETALLADO"],
                ...analisisIA.analisisDetallado.map(item => [`• ${item}`]),
                [""],
                ["RECOMENDACIONES"],
                ...analisisIA.recomendaciones.map(item => [`• ${item}`]),
                [""],
                ["ALERTAS Y PRECAUCIONES"],
                ...analisisIA.alertas.map(item => [`⚠ ${item}`]),
                [""],
                ["ACCIONES INMEDIATAS SUGERIDAS"],
                ...analisisIA.accionesInmediatas.map(item => [`✓ ${item}`])
            ];

            const wsAnalisis = XLSX.utils.aoa_to_sheet(analisisData);
            wsAnalisis['!cols'] = [{ wch: 100 }];
            XLSX.utils.book_append_sheet(wb, wsAnalisis, "Análisis IA");

            const datosHistoricos = [
                ["Fecha y Hora", "Valor", `Unidad (${unidad})`, "Estado del Sensor"],
                ...datos.map((d) => [
                    new Date(d.fecha_hora).toLocaleString(),
                    d.valor,
                    unidad,
                    sensor.estado
                ])
            ];

            const wsDatos = XLSX.utils.aoa_to_sheet(datosHistoricos);
            wsDatos['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];
            XLSX.utils.book_append_sheet(wb, wsDatos, "Datos Históricos");

            const datosGrafico = [
                ["Fecha", "Valor", "Promedio", "Mín Configurado", "Máx Configurado"],
                ...datos.map((d) => [
                    new Date(d.fecha_hora).toLocaleString("es-ES", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit"
                    }),
                    parseFloat(d.valor),
                    parseFloat(promedio),
                    sensor.valor_minimo ? parseFloat(sensor.valor_minimo) : "",
                    sensor.valor_maximo ? parseFloat(sensor.valor_maximo) : ""
                ])
            ];

            const wsGrafico = XLSX.utils.aoa_to_sheet(datosGrafico);
            wsGrafico['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }];
            XLSX.utils.book_append_sheet(wb, wsGrafico, "Datos para Gráfico");

            const estadisticasAvanzadas = [
                ["ANÁLISIS ESTADÍSTICO DETALLADO"],
                [""],
                ["Medidas de Tendencia Central"],
                ["Media (Promedio):", promedio, unidad],
                ["Mediana:", valores.length ? valores.sort((a, b) => a - b)[Math.floor(valores.length / 2)].toFixed(2) : "--", unidad],
                [""],
                ["Medidas de Dispersión"],
                ["Valor Mínimo:", minimo, unidad],
                ["Valor Máximo:", maximo, unidad],
                ["Rango:", valores.length ? (Math.max(...valores) - Math.min(...valores)).toFixed(2) : "--", unidad],
                [""],
                ["Análisis de Cumplimiento"],
                ["Lecturas Totales:", datos.length],
                ["Lecturas en Rango:", sensor.valor_minimo && sensor.valor_maximo ?
                    valores.filter(v => v >= sensor.valor_minimo && v <= sensor.valor_maximo).length : "N/A"],
                ["Lecturas Fuera de Rango:", sensor.valor_minimo && sensor.valor_maximo ?
                    valores.filter(v => v < sensor.valor_minimo || v > sensor.valor_maximo).length : "N/A"],
                [""],
                ["Análisis de Tendencia"],
                ["Tendencia General:", tendencia],
                ["Período Analizado:", rangoTiempo]
            ];

            const wsEstadisticas = XLSX.utils.aoa_to_sheet(estadisticasAvanzadas);
            wsEstadisticas['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 10 }];
            XLSX.utils.book_append_sheet(wb, wsEstadisticas, "Estadísticas");

            const nombreArchivo = `Analisis_Sensor_${sensor.sensor}_${new Date().toISOString().split("T")[0]}.xlsx`;
            XLSX.writeFile(wb, nombreArchivo);

            alert("✅ Reporte generado exitosamente con análisis de IA");

        } catch (error) {
            console.error("Error al exportar:", error);
            alert("❌ Error al generar el reporte. Por favor, intente nuevamente.");
        } finally {
            setExportando(false);
        }
    };

    if (cargando) {
        return (
            <div className="contenedor-panel contenedor-oscuro">
                <div className="contenedor-lateral">
                    <Sidebar isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
                </div>
                <div className="contenedor-principal">
                    <Header isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
                    <div className="contenedor-informacion">
                        <div className="mensaje-cargando">Cargando detalles del sensor...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (!sensor) {
        return (
            <div className="contenedor-panel contenedor-oscuro">
                <div className="contenedor-lateral">
                    <Sidebar isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
                </div>
                <div className="contenedor-principal">
                    <Header isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
                    <div className="contenedor-informacion">
                        <div className="mensaje-sin-datos">Sensor no encontrado</div>
                    </div>
                </div>
            </div>
        );
    }

    const unidad = UNIDADES[sensor.tipo_sensor?.toLowerCase()] || "";
    const IconoTipo = ICONOS_TIPO[sensor.tipo_sensor?.toLowerCase()] || Activity;
    const valorActual = sensor.valor ?? "--";
    const fueraRango =
        sensor.valor !== null &&
        sensor.valor !== undefined &&
        !isNaN(parseFloat(sensor.valor)) &&
        ((sensor.valor_minimo !== null &&
            parseFloat(sensor.valor) < parseFloat(sensor.valor_minimo)) ||
            (sensor.valor_maximo !== null &&
                parseFloat(sensor.valor) > parseFloat(sensor.valor_maximo)));

    const valores = datos.map((d) => parseFloat(d.valor)).filter((v) => !isNaN(v));
    const promedio = valores.length
        ? (valores.reduce((a, b) => a + b, 0) / valores.length).toFixed(2)
        : "--";
    const maximo = valores.length ? Math.max(...valores).toFixed(2) : "--";
    const minimo = valores.length ? Math.min(...valores).toFixed(2) : "--";

    let tendencia = null;
    if (valores.length >= 2) {
        const inicio = valores.slice(0, Math.floor(valores.length / 2));
        const fin = valores.slice(Math.floor(valores.length / 2));
        const promedioInicio = inicio.reduce((a, b) => a + b, 0) / inicio.length;
        const promedioFin = fin.reduce((a, b) => a + b, 0) / fin.length;
        tendencia = promedioFin > promedioInicio ? "up" : "down";
    }

    const datosGrafico = datos.map((d) => ({
        fecha: new Date(d.fecha_hora).toLocaleString("es-ES", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }),
        valor: parseFloat(d.valor),
        timestamp: new Date(d.fecha_hora).getTime(),
    }));

    return (
        <div className="contenedor-panel contenedor-oscuro">
            <div className="contenedor-lateral">
                <Sidebar isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
            </div>
            <div className="contenedor-principal">
                <Header isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
                <div className="contenedor-informacion">

                    <div className="header-sensor-detail">
                        <button
                            className="boton-volver"
                            onClick={() => navigate("/devices")}
                        >
                            <ArrowLeft size={20} />
                            Volver
                        </button>
                        <div className="acciones-sensor-detail">
                            <button
                                className={`boton-refrescar ${actualizando ? "girando" : ""}`}
                                onClick={handleRefresh}
                                disabled={actualizando}
                            >
                                <RefreshCw size={18} />
                            </button>
                            <button
                                className={`boton-exportar ${exportando ? "exportando" : ""}`}
                                onClick={exportarDatos}
                                disabled={exportando}
                            >
                                {!exportando && <Download size={18} />}
                                {exportando ? "Generando..." : "Exportar con IA"}
                            </button>
                        </div>
                    </div>

                    <div className="tarjeta-info-principal-sensor">
                        <div className="info-sensor-izquierda">
                            <div className="icono-sensor-grande">
                                <IconoTipo size={32} />
                            </div>
                            <div>
                                <h2 className="titulo-sensor-detail">{sensor.sensor}</h2>
                                <div className="meta-sensor-detail">
                                    <MapPin size={16} />
                                    {sensor.terreno}
                                </div>
                            </div>
                        </div>
                        <div className="info-sensor-derecha">
                            <div className="valor-actual-grande">
                                {valorActual}
                                <span className="unidad-grande">{unidad}</span>
                            </div>
                            <div
                                className={`estado-sensor-chip ${sensor.estado === "activo" ? "activo" : "inactivo"
                                    }`}
                            >
                                {sensor.estado === "activo" ? (
                                    <Wifi size={16} />
                                ) : (
                                    <WifiOff size={16} />
                                )}
                                {sensor.estado === "activo" ? "Activo" : "Inactivo"}
                            </div>
                            {fueraRango && (
                                <div className="alerta-sensor-badge">
                                    <AlertTriangle size={16} />
                                    Fuera de rango
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid-estadisticas-sensor">
                        <div className="tarjeta-estadistica">
                            <div className="icono-estadistica">
                                <Activity size={20} />
                            </div>
                            <div className="info-estadistica">
                                <div className="valor-estadistica">
                                    {promedio}
                                    <span className="unidad-estadistica">{unidad}</span>
                                </div>
                                <div className="label-estadistica">Promedio</div>
                            </div>
                        </div>
                        <div className="tarjeta-estadistica">
                            <div className="icono-estadistica">
                                <TrendingUp size={20} />
                            </div>
                            <div className="info-estadistica">
                                <div className="valor-estadistica">
                                    {maximo}
                                    <span className="unidad-estadistica">{unidad}</span>
                                </div>
                                <div className="label-estadistica">Máximo</div>
                            </div>
                        </div>
                        <div className="tarjeta-estadistica">
                            <div className="icono-estadistica">
                                <TrendingDown size={20} />
                            </div>
                            <div className="info-estadistica">
                                <div className="valor-estadistica">
                                    {minimo}
                                    <span className="unidad-estadistica">{unidad}</span>
                                </div>
                                <div className="label-estadistica">Mínimo</div>
                            </div>
                        </div>
                        <div className="tarjeta-estadistica">
                            <div className="icono-estadistica">
                                <Clock size={20} />
                            </div>
                            <div className="info-estadistica">
                                <div className="valor-estadistica">{datos.length}</div>
                                <div className="label-estadistica">Lecturas</div>
                            </div>
                        </div>
                    </div>

                    <div className="tarjeta-grafico-sensor">
                        <div className="header-grafico-sensor">
                            <h3>Histórico de Lecturas</h3>
                            <div className="selectores-rango">
                                <button
                                    className={`boton-rango ${rangoTiempo === "24h" ? "activo" : ""
                                        }`}
                                    onClick={() => setRangoTiempo("24h")}
                                >
                                    24h
                                </button>
                                <button
                                    className={`boton-rango ${rangoTiempo === "7d" ? "activo" : ""
                                        }`}
                                    onClick={() => setRangoTiempo("7d")}
                                >
                                    7d
                                </button>
                                <button
                                    className={`boton-rango ${rangoTiempo === "30d" ? "activo" : ""
                                        }`}
                                    onClick={() => setRangoTiempo("30d")}
                                >
                                    30d
                                </button>
                            </div>
                        </div>
                        {datosGrafico.length === 0 ? (
                            <div className="mensaje-sin-datos-grafico">
                                No hay datos disponibles para mostrar
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={400}>
                                <AreaChart data={datosGrafico}>
                                    <defs>
                                        <linearGradient
                                            id="colorValor"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="5%"
                                                stopColor="#6366f1"
                                                stopOpacity={0.8}
                                            />
                                            <stop
                                                offset="95%"
                                                stopColor="#6366f1"
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#3d4463" />
                                    <XAxis
                                        dataKey="fecha"
                                        stroke="#9ca3af"
                                        tick={{ fill: "#9ca3af" }}
                                    />
                                    <YAxis
                                        stroke="#9ca3af"
                                        tick={{ fill: "#9ca3af" }}
                                        label={{
                                            value: unidad,
                                            angle: -90,
                                            position: "insideLeft",
                                            fill: "#9ca3af",
                                        }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: "#252b48",
                                            border: "1px solid #3d4463",
                                            borderRadius: "8px",
                                        }}
                                        labelStyle={{ color: "#fff" }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="valor"
                                        stroke="#6366f1"
                                        fillOpacity={1}
                                        fill="url(#colorValor)"
                                    />
                                    {sensor.valor_minimo && (
                                        <Line
                                            y={sensor.valor_minimo}
                                            stroke="#ff9800"
                                            strokeDasharray="5 5"
                                        />
                                    )}
                                    {sensor.valor_maximo && (
                                        <Line
                                            y={sensor.valor_maximo}
                                            stroke="#ff9800"
                                            strokeDasharray="5 5"
                                        />
                                    )}
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="grid-info-adicional">
                        <div className="tarjeta-info-adicional">
                            <h4>Configuración</h4>
                            <div className="lista-config">
                                <div className="item-config">
                                    <span className="label-config">Tipo de sensor:</span>
                                    <span className="valor-config">{sensor.tipo_sensor}</span>
                                </div>
                                <div className="item-config">
                                    <span className="label-config">Valor mínimo:</span>
                                    <span className="valor-config">
                                        {sensor.valor_minimo ?? "No definido"}
                                        {sensor.valor_minimo && ` ${unidad}`}
                                    </span>
                                </div>
                                <div className="item-config">
                                    <span className="label-config">Valor máximo:</span>
                                    <span className="valor-config">
                                        {sensor.valor_maximo ?? "No definido"}
                                        {sensor.valor_maximo && ` ${unidad}`}
                                    </span>
                                </div>
                                <div className="item-config">
                                    <span className="label-config">Última actualización:</span>
                                    <span className="valor-config">
                                        {sensor.fecha_hora
                                            ? new Date(sensor.fecha_hora).toLocaleString()
                                            : "Sin datos"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {tendencia && (
                            <div className="tarjeta-info-adicional">
                                <h4>Tendencia</h4>
                                <div className="contenedor-tendencia">
                                    {tendencia === "up" ? (
                                        <>
                                            <TrendingUp size={48} className="icono-tendencia-up" />
                                            <p>
                                                Los valores muestran una tendencia al alza en el
                                                período seleccionado
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <TrendingDown
                                                size={48}
                                                className="icono-tendencia-down"
                                            />
                                            <p>
                                                Los valores muestran una tendencia a la baja en el
                                                período seleccionado
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}